// components/NetWorthGraph.tsx
import React, { useMemo, useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { fetchTotalsSums, TotalsSum } from '../services/totalsService';
import { DEFAULT_USER_ID } from '../helpers/HelperConstants';

export type DateRange = '7d' | '30d' | '3m' | '6m' | '1y' | 'all';

interface NetWorthGraphProps {
  /** Current net worth value — used to anchor the mock time-series */
  netWorth: number;
}

const RANGE_LABELS: Record<DateRange, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '3m':  'Last 3 months',
  '6m': 'Last 6 months',
  '1y':  'Last 1 year',
  'all': 'ALL',
};

function formatCurrency(value: number): string {
  if (Math.abs(value) >= 1_000_000_000_000_000)
    return `$${(value / 1_000_000_000_000_000).toFixed(1)}Q`;
  if (Math.abs(value) >= 1_000_000_000_000)
    return `$${(value / 1_000_000_000_000).toFixed(1)}T`;
  if (Math.abs(value) >= 1_000_000_000)
    return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(value) >= 1_000_000)
    return `$${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000)
    return `$${(value / 1_000).toFixed(0)}k`;
  return `$${value}`;
}

function formatTickDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day:   'numeric',
    year:  '2-digit',
  });
}

const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: 'rgba(15,17,23,0.92)',
        border: '1px solid rgba(93,224,245,0.25)',
        borderRadius: 8,
        padding: '8px 14px',
        fontSize: 13,
        color: '#f0f4f8',
      }}
    >
      <div style={{ color: 'rgba(240,244,248,0.5)', marginBottom: 2, fontSize: 11 }}>
        {formatTickDate(label)}
      </div>
      <div style={{ fontWeight: 600 }}>
        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
          payload[0].value
        )}
      </div>
    </div>
  );
};

const NetWorthGraph: React.FC<NetWorthGraphProps> = () => {
  const [range, setRange] = useState<DateRange>('30d');
  const [allData, setAllData] = useState<TotalsSum[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchTotalsSums(DEFAULT_USER_ID)
      .then((result) => {setAllData(result); })
      .catch((err: Error) => { console.error('Error fetching totals sums:', err); setError('Error getting the total sums.'); })
      .finally(() => { setLoading(false); });
  }, []);

  const data = useMemo(() => {
    if (loading || error) return [];
    const now = new Date();
    const cutoff = new Date(now);
    if (range === '7d') cutoff.setDate(now.getDate() - 7);
    else if (range === '30d') cutoff.setDate(now.getDate() - 30);
    else if (range === '3m') cutoff.setMonth(now.getMonth() - 3);
    else if (range === '6m') cutoff.setMonth(now.getMonth() - 6);
    else if (range === '1y') cutoff.setFullYear(now.getFullYear() - 1);
    else if (range === 'all') cutoff.setFullYear(1970); // arbitrary early date

    const sorted = [...allData].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const inRange = sorted
      .filter((item) => new Date(item.date) >= cutoff)
      .map((item) => ({ date: item.date, value: item.value }));

    if (inRange.length > 0) { return inRange; }

    const lastPoint = sorted[sorted.length - 1];
    if (!lastPoint) return [];
 
    const todayISO = now.toISOString().split('T')[0];
 
    // If the last known date is already today, return a single point
    if (lastPoint.date === todayISO) {
      return [{ date: lastPoint.date, value: lastPoint.value }];
    }
 
    // Two points: last known entry + today with the same value = flat line
    return [
      { date: lastPoint.date, value: lastPoint.value },
      { date: todayISO,       value: lastPoint.value },
    ];
  }, [allData, loading, error, range]);


  // Dynamic tick interval - scales to however many points are in view.
  // Aim for 6-8 visible labels regardless of data density.
  const tickInterval = useMemo(() => {
    const targetTickCount = 7;
    if (data.length <= targetTickCount) return 0; // show every label
    return Math.ceil(data.length / targetTickCount) - 1;
  }, [data.length]);

  // Determine tick interval so X-axis doesn't get crowded
  // const tickEvery = range === '30d' ? 6 : range === '3m' ? 4 : 5;

  return (
    <div>
      <div className="graph-container">
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="4 4" />
            <XAxis
              dataKey="date"
              tick={{ fill: 'rgba(240,244,248,0.45)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              interval={tickInterval}
            />
            <YAxis
              tickFormatter={formatCurrency}
              tick={{ fill: 'rgba(240,244,248,0.45)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#5de0f5"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: '#5de0f5', strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="graph-range-tabs">
        {(Object.keys(RANGE_LABELS) as DateRange[]).map((r) => (
          <button
            key={r}
            className={`range-tab ${range === r ? 'active' : ''}`}
            onClick={() => setRange(r)}
          >
            {RANGE_LABELS[r]}
          </button>
        ))}
      </div>
    </div>
  );
};

export default NetWorthGraph;
